import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { VIBE_CHAT_API_BASE } from '@/lib/vibeChatConfig';
import ChatDeliverablePanel from '@/components/ChatDeliverablePanel';
import PrdArtifactCard from '@/components/PrdArtifactCard';
import WorkflowTaskGraph from '@/components/WorkflowTaskGraph';
import {
  WORKFLOW_STAGE,
  WORKFLOW_STAGE_LABELS,
  getWorkflowStageLabel,
  getWorkflowSteps,
  defaultNupSteps,
  defaultTerSteps,
  defaultPwdResetSteps,
  getUserConfirmation,
  resolveWorkflowType,
  WORKFLOW_TYPE_TER,
  WORKFLOW_TYPE_PWD_RESET,
} from '@/lib/workflowHelpers';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { getConfirmDocLabels } from '@/lib/confirmDocLabels';
import { chatWorkingIndicatorLabel, chatMessageDisplayOpts, displayChatMessageContent, isHiddenChatMessage, CHAT_THREAD_SCROLL_CLASS, isChatScrollPinnedToBottom, isProcessingPlaceholder, parseConnectedPreference, shouldShowChatWorkingIndicator, STUDIO_WELCOME_MESSAGE, sanitizeRailArtifactSummary, shouldHideAssistantWhileGenerating } from '@/lib/chatDisplay';
import { integrationSuiteCopyPayload } from '@/components/IntegrationSuitePanelBlocks';
import {
  isIntegrationSuiteDeliverable,
  isSecurityDeliverable,
  mergeDeliverableMeta,
  resolveCopyPayload,
  resolveDeliverableKind,
  resolveReportUrl,
  resolveIntentUiMeta,
  deliverableLabelFromMeta,
  intentRequiresConnected,
  inferIntentFromUserMessage,
} from '@/lib/chatDeliverable';
import {
  artifactHasContent,
  contentfulArtifacts,
  deliverableTabLabelForArtifact,
  deliverableFromArtifact,
  hasRailDeliverableContent,
  integrationSuiteRailBlocks,
  isChatDeliverableMetaInFlight,
  normalizeArtifactsList,
  reconcileSessionArtifacts,
  mergeSessionArtifactRows,
  railTabArtifacts,
  inFlightArtifactPlaceholderId,
  resolveActiveArtifact,
  resolveStickyChatIntent,
} from '@/lib/chatArtifacts';
import { fetchGeneralSettings } from '@/lib/generalSettingsApi';
import { deployChatStudioCode } from '@/lib/abapDeployApi';
import {
  useChatPrdAvailable,
  shouldShowPrdRailTab,
  resolvePrdReady,
} from '@/hooks/useChatPrdAvailable';
import {
  formatApiError,
  loadChatSessionThread,
  mapChatResponse,
  normalizeSessionsPayload,
  stripCodeFences,
  sessionIdOf,
  phaseOf,
  previewOf,
  dateOf,
  titleOf,
  normalizeChatTitle,
  renameChatSession,
  readActiveChatSessionIdFromStorage,
  persistActiveChatSessionId,
  clearActiveChatSessionId,
  inferGeneratingFromThreadMeta,
} from '@/lib/vibeChatApi';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Brain,
  Sparkles,
  Loader2,
  RefreshCw,
  MessageSquarePlus,
  PanelLeft,
  PanelRight,
  AlertCircle,
  Activity,
  Send,
  Code2,
  FileSpreadsheet,
  FileText,
  Pencil,
  Check,
  CheckCircle2,
  ShieldAlert,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  GripVertical,
} from 'lucide-react';

const SESSIONS_LIMIT = 50;
const LS_SESSIONS_OPEN = 'vibe-studio-sessions-open';
const LS_RAIL_OPEN = 'vibe-studio-rail-open';
const LS_RAIL_WIDTH = 'vibe-studio-rail-width';

function readLocalBool(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    return v === '1' || v === 'true';
  } catch {
    return fallback;
  }
}

function writeLocalBool(key, value) {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function readLocalNum(key) {
  try {
    const n = Number(localStorage.getItem(key));
    // Ignore older narrow defaults (<=340) so large screens pick up the wider clamp.
    if (!Number.isFinite(n) || n <= 340 || n > 640) return null;
    return n >= 280 ? n : null;
  } catch {
    return null;
  }
}

function writeLocalNum(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, String(Math.round(value)));
  } catch {
    /* ignore */
  }
}

function AssistantWorkingIndicator({ label }) {
  return (
    <div
      className="flex gap-3 pl-1"
      role="status"
      aria-live="polite"
      aria-label={label || 'Assistant is working'}
    >
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-cp-grad shadow-lg ring-2 ring-cp-violet/40">
        <Loader2 className="h-4 w-4 animate-spin text-white" aria-hidden />
      </div>
      <div className="flex-1 rounded-2xl border-2 border-cp-line bg-cp-inset p-4 shadow-md">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-cp-fg">Assistant</span>
          <TypingDots />
        </div>
        {label ? <p className="mt-1 text-xs text-cp-violet">{label}</p> : null}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-1 py-0.5" aria-hidden>
      {[0, 200, 400].map((delay) => (
        <span
          key={delay}
          className="h-2 w-2 rounded-full bg-cp-violet/70 opacity-70"
          style={{ animation: 'vibeWsDot 1.4s infinite ease-in-out', animationDelay: `${delay}ms` }}
        />
      ))}
      <style>{`
        @keyframes vibeWsDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function ThreadBubble({ role, content, index, displayContent }) {
  const text = displayContent ?? content;
  const isUser = role === 'user';
  return (
    <div
      className={cn(
        'vibe-ws-msg flex min-w-0 gap-3 opacity-0',
        isUser && 'flex-row-reverse'
      )}
      style={{
        animation: 'vibeWsMsgIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards',
        animationDelay: `${Math.min(index, 20) * 40}ms`,
      }}
    >
      <div
        className={cn(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-lg ring-2 ring-offset-2 ring-offset-[var(--surface)]',
          isUser
            ? 'bg-cp-grad ring-cp-indigo/40'
            : 'bg-cp-grad ring-cp-violet/40/80'
        )}
      >
        {isUser ? 'You' : <Brain className="h-4 w-4" />}
      </div>
      <div
        className={cn(
          'min-w-0 flex-1 rounded-2xl border px-4 py-3 shadow-md transition-transform duration-300 hover:scale-[1.01]',
          isUser
            ? 'border-cp-line bg-cp-inset'
            : 'border-cp-line bg-cp-surface backdrop-blur-md'
        )}
      >
        <div
          className={cn(
            'mb-1 text-[10px] font-bold uppercase tracking-widest',
            isUser ? 'text-cp-indigo' : 'text-cp-violet'
          )}
        >
          {isUser ? 'You' : 'Assistant'}
        </div>
        <div
          className="whitespace-pre-wrap break-words text-sm leading-relaxed text-cp-fg"
          style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
        >
          {text}
        </div>
      </div>
    </div>
  );
}

function usePhaseVisual(phase, chatFinished, chatAwaitingUser, hasSession, intent, confirmDocType) {
  return useMemo(() => {
    const p = (phase || '').toUpperCase();
    const confirmLabels = getConfirmDocLabels({ intent, confirmDocType });
    if (chatFinished || p === 'DONE') {
      const intentNorm = String(intent || 'abap').toLowerCase();
      const sub = isSecurityDeliverable(intentNorm)
        ? 'Security results ready when complete.'
        : intentNorm === 'static_report'
          ? 'Static report ready when complete.'
          : isIntegrationSuiteDeliverable(intentNorm)
            ? 'Integration Suite — keep chatting or start New chat.'
          : intentNorm === 'workflow' || intentNorm === 'nup' || intentNorm === 'new_user_provisioning'
            ? 'Workflow finished — continue in chat or start New chat.'
            : 'ABAP ready when generated.';
      return {
        progress: 100,
        headline: 'Complete',
        sub,
        badgeClass: 'border-cp-ok bg-cp-inset text-cp-ok',
        bar: 'from-cp-ok to-cp-teal',
      };
    }
    if (p === 'CONFIRM') {
      const intentNorm = String(intent || 'abap').toLowerCase();
      if (intentNorm === 'static_report') {
        return {
          progress: 88,
          headline: 'Generating report',
          sub: 'Building your HTML report from SAP data — stay on this page.',
          badgeClass: 'border-cp-indigo bg-cp-inset text-cp-indigo',
          bar: 'from-cp-indigo to-cp-violet',
        };
      }
      return {
        progress: 72,
        headline: 'Confirm',
        sub: confirmLabels.confirmHint,
        badgeClass: 'border-cp-mod bg-cp-inset text-cp-mod',
        bar: 'from-cp-mod to-cp-imp',
      };
    }
    if (p === 'GATHER') {
      return {
        progress: 38,
        headline: 'Gathering',
        sub: 'Clarifying your requirements.',
        badgeClass: 'border-cp-violet bg-cp-inset text-cp-violet',
        bar: 'from-cp-violet to-cp-indigo',
      };
    }
    if (p === 'GENERATE' || p === 'GENERATING') {
      const intentNorm = String(intent || 'abap').toLowerCase();
      if (isIntegrationSuiteDeliverable(intentNorm)) {
        return {
          progress: 72,
          headline: 'Integration Suite',
          sub: 'Running BTP tools for your request…',
          badgeClass: 'border-cp-indigo bg-cp-inset text-cp-indigo',
          bar: 'from-cp-indigo to-cp-violet',
        };
      }
    }
    if (hasSession && chatAwaitingUser) {
      return {
        progress: 55,
        headline: 'Your turn',
        sub: 'Reply below.',
        badgeClass: 'border-cp-indigo bg-cp-inset text-cp-fg',
        bar: 'from-cp-indigo to-cp-violet',
      };
    }
    if (hasSession) {
      return {
        progress: 28,
        headline: 'Running',
        sub: 'Follow the thread.',
        badgeClass: 'border-cp-line bg-cp-inset text-cp-fg',
        bar: 'from-cp-violet to-cp-indigo',
      };
    }
    return {
      progress: 0,
      headline: 'Idle',
      sub: 'Pick a session or start new.',
      badgeClass: 'border-cp-line bg-cp-inset text-cp-muted',
      bar: 'from-cp-faint to-cp-muted',
    };
  }, [phase, chatFinished, chatAwaitingUser, hasSession, intent, confirmDocType]);
}

export default function VibeChatWorkspace() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  // Do not use vibe-studio.write here — it treats delete as a mutation and unlocks chat UI.
  const canCreateChat = hasPermission('vibe-studio', 'create');
  const canSendMessage = canCreateChat || hasPermission('vibe-studio', 'update');
  const canRenameChat = canSendMessage;
  const canDeployChat = canSendMessage;
  // Same gate as Trigger Workflow / ticket detail resolve.
  const canResolveAccessIssue = hasPermission('tickets', 'update');
  const resolverIdentity = user?.email || user?.username || 'Current User';

  const [searchParams, setSearchParams] = useSearchParams();
  const urlSessionId = searchParams.get('session');

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsLoadingMore, setSessionsLoadingMore] = useState(false);
  const [sessionsHasMore, setSessionsHasMore] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);
  const [sessionSearchInput, setSessionSearchInput] = useState('');
  const [sessionQuery, setSessionQuery] = useState('');
  const sessionsOffsetRef = useRef(0);
  const sessionsLoadingMoreRef = useRef(false);
  const sessionsGenerationRef = useRef(0);
  const sessionsAppendAbortRef = useRef(null);
  const sessionsScrollRef = useRef(null);
  const sessionsLoadMoreSentinelRef = useRef(null);

  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);

  const [chatAwaitingUser, setChatAwaitingUser] = useState(false);
  const [chatPhase, setChatPhase] = useState('');
  const [chatIntent, setChatIntent] = useState('unclear');
  const [chatConfirmDocType, setChatConfirmDocType] = useState(null);
  const [artifactType, setArtifactType] = useState(null);
  const [artifactUrl, setArtifactUrl] = useState(null);
  const [chatGeneratedCode, setChatGeneratedCode] = useState(null);
  const [artifactSummary, setArtifactSummary] = useState(null);
  const [panelBlocks, setPanelBlocks] = useState([]);
  const [sessionArtifacts, setSessionArtifacts] = useState([]);
  const [selectedArtifactId, setSelectedArtifactId] = useState(null);
  const [workflowStage, setWorkflowStage] = useState(null);
  const [workflowState, setWorkflowState] = useState(null);
  const [mirroredTicketNumber, setMirroredTicketNumber] = useState(null);
  const [chatAwaitingAction, setChatAwaitingAction] = useState(null);
  const [chainOfThought, setChainOfThought] = useState('');
  const [resolveComment, setResolveComment] = useState('');
  const [resolveSubmitting, setResolveSubmitting] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);

  const [chatGenerating, setChatGenerating] = useState(false);
  const [pendingTurnIntent, setPendingTurnIntent] = useState(null);
  const pendingTurnIntentRef = useRef(null);
  const [chatIntentMeta, setChatIntentMeta] = useState(null);
  const [prdAvailableHint, setPrdAvailableHint] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => readLocalBool(LS_SESSIONS_OPEN, true));
  const [railOpen, setRailOpen] = useState(() => readLocalBool(LS_RAIL_OPEN, true));
  const [workflowBannerOpen, setWorkflowBannerOpen] = useState(true);
  const [railTab, setRailTab] = useState('prd'); // 'prd' | 'code'
  const [chainExpanded, setChainExpanded] = useState(false);
  const [railWidthPx, setRailWidthPx] = useState(() => readLocalNum(LS_RAIL_WIDTH));
  const [railResizing, setRailResizing] = useState(false);
  const railPanelRef = useRef(null);
  const resizeDragRef = useRef({ startX: 0, startW: 300 });
  /** Avoid re-focusing the latest deliverable tab on every status poll after the user picks another. */
  const deliverableRailFocusRef = useRef({ sessionId: null, artifactCount: 0 });
  /** User explicitly picked a deliverable pill — do not override from status poll / active_artifact_id. */
  const manualArtifactTabRef = useRef({ sessionId: null, artifactId: null });

  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatStarting, setChatStarting] = useState(false);
  const [startError, setStartError] = useState(null);
  const chatInputRef = useRef(null);

  const [useStudioContext, setUseStudioContext] = useState(true);
  const [platformModeLocked, setPlatformModeLocked] = useState(false);
  const [platformModeReady, setPlatformModeReady] = useState(false);
  const useStudioContextRef = useRef(true);
  useEffect(() => {
    useStudioContextRef.current = useStudioContext;
  }, [useStudioContext]);

  const [deployAvailable, setDeployAvailable] = useState(false);
  const [codeValidated, setCodeValidated] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployMessage, setDeployMessage] = useState(null);

  const [chatExcelFile, setChatExcelFile] = useState(null);
  const chatFileInputRef = useRef(null);

  const [renamingSessionId, setRenamingSessionId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState(null);

  const applyPlatformConnectedState = useCallback((mode) => {
    const normalized = String(mode || 'connected').toLowerCase() === 'disconnected' ? 'disconnected' : 'connected';
    const connected = normalized === 'connected';
    setUseStudioContext(connected);
    useStudioContextRef.current = connected;
  }, []);

  // Load the org's default connected mode so new chats and replies send the
  // correct use_studio_context, instead of assuming "connected" for everyone.
  // Re-checked on tab focus so an admin toggling the org setting mid-session
  // is picked up without requiring a refresh.
  useEffect(() => {
    let cancelled = false;
    const loadPlatformMode = () => {
      fetchGeneralSettings()
        .then((data) => {
          if (cancelled) return;
          applyPlatformConnectedState(data?.chat_connected_mode || data?.default_connected_mode || 'connected');
          setPlatformModeReady(true);
        })
        .catch(() => {
          if (!cancelled) setPlatformModeReady(true);
        });
    };
    loadPlatformMode();
    window.addEventListener('focus', loadPlatformMode);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', loadPlatformMode);
    };
  }, [applyPlatformConnectedState]);

  const isPlatformConnected = platformModeReady && useStudioContext;

  const threadEndRef = useRef(null);
  const threadScrollRef = useRef(null);
  const threadPinnedToBottomRef = useRef(true);
  const activeThreadRef = useRef(null);
  // Mirror selectedId into a ref so effects and callbacks can read the current
  // value without taking it as a dependency (prevents URL-sync loop).
  const selectedIdRef = useRef(null);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  const selectedArtifactIdRef = useRef(null);
  useEffect(() => {
    selectedArtifactIdRef.current = selectedArtifactId;
  }, [selectedArtifactId]);
  const sessionArtifactsRef = useRef([]);
  const panelBlocksRef = useRef([]);
  useEffect(() => {
    sessionArtifactsRef.current = sessionArtifacts;
  }, [sessionArtifacts]);
  useEffect(() => {
    panelBlocksRef.current = panelBlocks;
  }, [panelBlocks]);
  useEffect(() => {
    pendingTurnIntentRef.current = pendingTurnIntent;
  }, [pendingTurnIntent]);
  // Track which session IDs have already played their entry animation so that
  // deselecting a session does not re-trigger the fade-from-zero animation.
  const animatedSessionsRef = useRef(new Set());

  const sessionSupportsPrd = useMemo(() => {
    if (prdAvailableHint) return true;
    const intentNorm = String(chatIntent || '').toLowerCase();
    if (['abap', 'static_report'].includes(intentNorm)) return true;
    return sessionArtifacts.some((row) => {
      const k = String(row.type || row.intent || '').toLowerCase();
      return k === 'abap' || k === 'static_report';
    });
  }, [chatIntent, prdAvailableHint, sessionArtifacts]);

  const { prdAvailable, prdChecking, markPrdAvailable } = useChatPrdAvailable(
    selectedId,
    prdAvailableHint,
    chatIntent,
    chatPhase,
    sessionSupportsPrd
  );
  const prdReady = resolvePrdReady({
    prdAvailable,
    prdAvailableHint,
    phase: chatPhase,
  });

  const applyPrdFlag = (m) => {
    if (m?.prdAvailable) {
      setPrdAvailableHint(true);
      markPrdAvailable();
    }
  };

  const chatFinished = String(chatPhase).toUpperCase() === 'DONE';

  // While a session thread is loading, do not pretend the intent is ABAP — that
  // briefly shows the wrong rail/footer after switching from NUP/security chats.
  const liveTurnIntent = useMemo(() => {
    const pending = String(pendingTurnIntent || '').toLowerCase();
    if ((chatGenerating || chatSending) && pending && pending !== 'unclear') {
      return pending;
    }
    if (chatIntent && chatIntent !== 'unclear') {
      return chatIntent;
    }
    return '';
  }, [pendingTurnIntent, chatGenerating, chatSending, chatIntent]);

  const railArtifactPills = useMemo(
    () =>
      railTabArtifacts(sessionArtifacts, {
        inFlight: chatGenerating || chatSending,
        pendingIntent: liveTurnIntent,
      }),
    [sessionArtifacts, chatGenerating, chatSending, liveTurnIntent]
  );

  const railArtifact = useMemo(
    () => resolveActiveArtifact(railArtifactPills, selectedArtifactId),
    [railArtifactPills, selectedArtifactId]
  );

  const multiDeliverableRailTabs = railArtifactPills.length > 1;

  const sessionIntent =
    liveTurnIntent ||
    (threadLoading ? '' : '');

  const userPinnedDeliverableTab = useMemo(() => {
    const sid = selectedId;
    const aid = selectedArtifactId;
    if (!sid || !aid) return false;
    const manual = manualArtifactTabRef.current;
    return manual.sessionId === sid && manual.artifactId === aid;
  }, [selectedId, selectedArtifactId]);

  const railDeliverable = useMemo(
    () =>
      deliverableFromArtifact(
        railArtifact,
        {
          intent: sessionIntent,
          artifactType,
          generatedCode: chatGeneratedCode,
          artifactSummary,
          panelBlocks,
          artifactUrl,
          workflowStage,
          workflowState,
        },
        { strict: userPinnedDeliverableTab && Boolean(railArtifact) }
      ),
    [
      railArtifact,
      sessionIntent,
      artifactType,
      chatGeneratedCode,
      artifactSummary,
      panelBlocks,
      artifactUrl,
      workflowStage,
      workflowState,
      userPinnedDeliverableTab,
    ]
  );

  const inFlightArtifactType = (chatGenerating || chatSending) && artifactType ? artifactType : null;
  const selectedRailKind = String(railArtifact?.type || railArtifact?.intent || '').toLowerCase();
  const panelFollowsInFlightTurn =
    Boolean(inFlightArtifactType) &&
    (!railArtifact ||
      railArtifact.inFlightPlaceholder ||
      selectedRailKind === String(inFlightArtifactType).toLowerCase() ||
      !multiDeliverableRailTabs);
  const effectiveIntent = panelFollowsInFlightTurn
    ? liveTurnIntent || railDeliverable.intent || sessionIntent
    : railDeliverable.intent || sessionIntent || liveTurnIntent;
  const panelArtifactType = panelFollowsInFlightTurn
    ? inFlightArtifactType
    : railDeliverable.artifactType ||
      artifactType ||
      (effectiveIntent && effectiveIntent !== 'abap' && effectiveIntent !== 'unclear'
        ? effectiveIntent
        : null);
  const railGeneratedCode = railDeliverable.generatedCode ?? chatGeneratedCode;
  const railArtifactSummary = railDeliverable.artifactSummary ?? artifactSummary;
  const railPanelBlocks = useMemo(() => {
    const raw =
      railDeliverable.panelBlocks?.length ? railDeliverable.panelBlocks : panelBlocks;
    if (
      isIntegrationSuiteDeliverable(effectiveIntent) ||
      isIntegrationSuiteDeliverable(panelArtifactType)
    ) {
      return integrationSuiteRailBlocks(raw, railArtifactSummary);
    }
    return raw;
  }, [
    railDeliverable.panelBlocks,
    panelBlocks,
    effectiveIntent,
    panelArtifactType,
    railArtifactSummary,
  ]);
  const railArtifactUrl = railDeliverable.artifactUrl ?? artifactUrl;
  const railWorkflowStage = railDeliverable.workflowStage ?? workflowStage;
  const railWorkflowState = railDeliverable.workflowState ?? workflowState;

  const resolvedArtifactUrl = useMemo(
    () => resolveReportUrl(railArtifactUrl, railGeneratedCode),
    [railArtifactUrl, railGeneratedCode]
  );

  const deliverableKind =
    threadLoading && (!chatIntent || chatIntent === 'unclear') && !liveTurnIntent
      ? 'pending'
      : resolveDeliverableKind(effectiveIntent, panelArtifactType, railGeneratedCode);

  const intentUi = useMemo(
    () =>
      resolveIntentUiMeta(
        effectiveIntent,
        chatIntentMeta && String(chatIntentMeta.id || '').toLowerCase() === String(effectiveIntent || '').toLowerCase()
          ? chatIntentMeta
          : null
      ),
    [effectiveIntent, chatIntentMeta]
  );
  const needsConnectedMode = intentRequiresConnected(intentUi) && !isPlatformConnected;

  const hasUserMessages = messages.some((m) => m.role === 'user');
  const showWorkingIndicator = shouldShowChatWorkingIndicator({
    sending: chatSending,
    generating: chatGenerating,
    hasUserMessages,
    phase: chatPhase,
  });

  const generatingLabel = chatWorkingIndicatorLabel({
    sending: chatSending,
    generating: showWorkingIndicator && chatGenerating,
    phase: chatPhase,
  });

  const doneFooterMessage = (() => {
    if (deliverableKind === 'security') {
      return 'Security results ready — send a message to refine, or use New chat for another request.';
    }
    if (deliverableKind === 'static_report') {
      return 'Report ready — open it from the panel on the right, or start New chat for another report.';
    }
    if (deliverableKind === 'workflow') {
      return 'Workflow ready — reply in chat to continue, or use New chat for another request.';
    }
    if (deliverableKind === 'integration_suite') {
      return 'Integration Suite turn complete — keep chatting to refine or run more tools.';
    }
    return 'ABAP ready — send a message to revise, or use New chat for another program.';
  })();

  const phaseVisual = usePhaseVisual(
    chatPhase,
    chatFinished,
    chatAwaitingUser,
    Boolean(selectedId),
    effectiveIntent,
    chatConfirmDocType
  );

  const handleThreadScroll = () => {
    threadPinnedToBottomRef.current = isChatScrollPinnedToBottom(threadScrollRef.current);
  };

  useEffect(() => {
    if (!threadPinnedToBottomRef.current) return;
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, chatGenerating, selectedId]);

  const loadSessions = useCallback(async ({ soft = false } = {}) => {
    if (!VIBE_CHAT_API_BASE) {
      setSessionsLoading(false);
      setSessionsError('Chat API URL not configured.');
      return;
    }
    const generation = soft
      ? sessionsGenerationRef.current
      : (++sessionsGenerationRef.current);
    if (!soft) {
      if (sessionsAppendAbortRef.current) {
        sessionsAppendAbortRef.current.abort();
        sessionsAppendAbortRef.current = null;
      }
      sessionsLoadingMoreRef.current = false;
      setSessionsLoadingMore(false);
      setSessionsLoading(true);
    }
    setSessionsError(null);
    try {
      const params = { limit: SESSIONS_LIMIT, offset: 0 };
      // Soft refresh / poll must not send include_total (COUNT storm).
      if (!soft) params.include_total = true;
      if (sessionQuery) params.q = sessionQuery;
      const { data } = await axios.get(`${VIBE_CHAT_API_BASE}/chat/sessions`, { params });
      if (generation !== sessionsGenerationRef.current) return;
      const { items, has_more: hasMore } = normalizeSessionsPayload(data);
      if (soft) {
        // Discard soft has_more — only append / hard reset may update pagination state.
        setSessions((prev) => {
          const byId = new Map(prev.map((r) => [sessionIdOf(r), r]));
          items.forEach((r) => {
            const id = sessionIdOf(r);
            if (id) byId.set(id, { ...(byId.get(id) || {}), ...r });
          });
          const page1Ids = new Set(items.map((r) => sessionIdOf(r)).filter(Boolean));
          const deeper = prev.filter((r) => {
            const id = sessionIdOf(r);
            return id && !page1Ids.has(id);
          });
          return [...items, ...deeper];
        });
      } else {
        setSessions(items);
        sessionsOffsetRef.current = SESSIONS_LIMIT;
        setSessionsHasMore(Boolean(hasMore));
      }
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
        return;
      }
      if (generation !== sessionsGenerationRef.current) return;
      setSessionsError(formatApiError(err));
      if (!soft) setSessions([]);
    } finally {
      if (generation === sessionsGenerationRef.current) setSessionsLoading(false);
    }
  }, [sessionQuery]);

  const loadMoreSessions = useCallback(async () => {
    if (!VIBE_CHAT_API_BASE || sessionsLoadingMoreRef.current || !sessionsHasMore) return;
    sessionsLoadingMoreRef.current = true;
    setSessionsLoadingMore(true);
    const generation = sessionsGenerationRef.current;
    const controller = new AbortController();
    sessionsAppendAbortRef.current = controller;
    try {
      const params = {
        limit: SESSIONS_LIMIT,
        offset: sessionsOffsetRef.current,
      };
      if (sessionQuery) params.q = sessionQuery;
      const { data } = await axios.get(`${VIBE_CHAT_API_BASE}/chat/sessions`, {
        params,
        signal: controller.signal,
      });
      if (generation !== sessionsGenerationRef.current) return;
      const { items, has_more: hasMore } = normalizeSessionsPayload(data);
      setSessions((prev) => {
        const seen = new Set(prev.map((r) => sessionIdOf(r)).filter(Boolean));
        return [...prev, ...items.filter((r) => {
          const id = sessionIdOf(r);
          return id && !seen.has(id);
        })];
      });
      sessionsOffsetRef.current += SESSIONS_LIMIT;
      setSessionsHasMore(Boolean(hasMore));
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
        return;
      }
      if (generation === sessionsGenerationRef.current) setSessionsError(formatApiError(err));
    } finally {
      if (sessionsAppendAbortRef.current === controller) sessionsAppendAbortRef.current = null;
      if (generation === sessionsGenerationRef.current) {
        sessionsLoadingMoreRef.current = false;
        setSessionsLoadingMore(false);
      }
    }
  }, [sessionQuery, sessionsHasMore]);

  useEffect(() => {
    loadSessions();
    return () => {
      if (sessionsAppendAbortRef.current) {
        sessionsAppendAbortRef.current.abort();
        sessionsAppendAbortRef.current = null;
      }
    };
  }, [loadSessions]);

  // Append-on-scroll in the sessions sidebar (root must be the sidebar scroll container).
  useEffect(() => {
    const el = sessionsLoadMoreSentinelRef.current;
    const root = sessionsScrollRef.current;
    if (!el || !sessionsHasMore) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMoreSessions();
      },
      { root: root || null, rootMargin: '120px 0px', threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [sessionsHasMore, loadMoreSessions, sessions.length]);

  const beginRenameSession = useCallback(
    (row) => {
      if (!canRenameChat) return;
      const id = sessionIdOf(row);
      if (!id) return;
      setRenamingSessionId(id);
      setRenameDraft(titleOf(row) || previewOf(row) || '');
      setRenameError(null);
    },
    [canRenameChat]
  );

  const cancelRenameSession = useCallback(() => {
    setRenamingSessionId(null);
    setRenameDraft('');
    setRenameError(null);
  }, []);

  const saveRenameSession = useCallback(
    async (sessionId) => {
      const id = String(sessionId || '').trim();
      if (!id || !VIBE_CHAT_API_BASE || renameSaving) return;
      const normalized = normalizeChatTitle(renameDraft);
      if (!normalized) {
        setRenameError('Title is required');
        return;
      }
      setRenameSaving(true);
      setRenameError(null);
      try {
        const result = await renameChatSession(VIBE_CHAT_API_BASE, id, normalized);
        setSessions((prev) =>
          prev.map((row) =>
            sessionIdOf(row) === id
              ? { ...row, title: result.title, title_user_set: true }
              : row
          )
        );
        cancelRenameSession();
      } catch (err) {
        setRenameError(formatApiError(err));
      } finally {
        setRenameSaving(false);
      }
    },
    [renameDraft, renameSaving, cancelRenameSession]
  );

  const applyMeta = useCallback((meta) => {
    const inFlight = isChatDeliverableMetaInFlight(meta);
    const stickyOpts = {
      inFlight,
      pendingIntent: pendingTurnIntentRef.current,
      metaArtifactType: meta?.artifact_type ?? meta?.artifactType,
    };
    const intentMetaPayload = meta?.intent_meta ?? meta?.intentMeta;
    if (intentMetaPayload && typeof intentMetaPayload === 'object') {
      setChatIntentMeta(intentMetaPayload);
    }
    if (meta?.phase != null && String(meta.phase) !== '') {
      setChatPhase(String(meta.phase).toUpperCase());
    }
    const au = meta?.awaiting_user ?? meta?.awaitingUser;
    if (au !== undefined && au !== null) {
      setChatAwaitingUser(Boolean(au));
    }
    const artifactRowsEarly = reconcileSessionArtifacts({
      ...meta,
      generating: meta?.generating ?? inFlight,
    });
    const pinned = deliverableRailFocusRef.current;
    const manualTab = manualArtifactTabRef.current;
    const sessionIdNow = selectedIdRef.current;
    const selectedAid = selectedArtifactIdRef.current;
    const selectedInSessionArtifacts = Boolean(
      selectedAid &&
        sessionArtifactsRef.current.some((row) => row.id === selectedAid)
    );
    const userChoseDeliverableTab =
      Boolean(sessionIdNow) &&
      Boolean(selectedAid) &&
      ((manualTab.sessionId === sessionIdNow && manualTab.artifactId === selectedAid) ||
        (pinned.sessionId === sessionIdNow &&
          (selectedInSessionArtifacts ||
            artifactRowsEarly.some((row) => row.id === selectedAid))));

    const gc = meta?.generated_code ?? meta?.generatedCode;
    if (!userChoseDeliverableTab && gc !== undefined && gc !== null) {
      const trimmed = String(gc).trim();
      if (trimmed) {
        setChatGeneratedCode(stripCodeFences(trimmed));
      } else if (!inFlight) {
        const pinned =
          sessionArtifactsRef.current.some((row) => artifactHasContent(row)) ||
          panelBlocksRef.current.length > 0;
        if (!pinned) {
          setChatGeneratedCode(null);
        }
      }
    }
    const intentHint = meta?.intent ?? meta?.artifact_type ?? meta?.artifactType;
    const summaryOpts = {
      intent: intentHint,
      artifactType: meta?.artifact_type ?? meta?.artifactType,
    };
    let sanitizedSummary;
    if (
      !userChoseDeliverableTab &&
      (meta?.artifact_summary !== undefined || meta?.artifactSummary !== undefined)
    ) {
      const summaryRaw = meta?.artifact_summary ?? meta?.artifactSummary;
      sanitizedSummary = sanitizeRailArtifactSummary(summaryRaw, summaryOpts);
      const summaryText =
        sanitizedSummary != null && String(sanitizedSummary).trim()
          ? String(sanitizedSummary)
          : null;
      if (summaryText) {
        setArtifactSummary(summaryText);
      } else if (!inFlight) {
        const pinned =
          sessionArtifactsRef.current.some((row) => artifactHasContent(row)) ||
          panelBlocksRef.current.length > 0;
        if (!pinned) {
          setArtifactSummary(null);
        }
      }
    }
    const panels = meta?.panel_blocks ?? meta?.panelBlocks;
    if (!userChoseDeliverableTab && Array.isArray(panels)) {
      if (panels.length > 0) {
        setPanelBlocks(panels);
      } else if (!inFlight) {
        const turnKind = String(meta?.artifact_type ?? meta?.artifactType ?? meta?.intent ?? '').toLowerCase();
        if (turnKind === 'static_report' || turnKind === 'abap') {
          setPanelBlocks([]);
        }
      }
    }
    const mergedArtifactRows = mergeSessionArtifactRows(
      sessionArtifactsRef.current,
      artifactRowsEarly,
      { inFlight }
    );
    if (mergedArtifactRows.length) {
      const rowsHaveContent = mergedArtifactRows.some((row) => artifactHasContent(row));
      if (rowsHaveContent || !inFlight) {
        setSessionArtifacts(mergedArtifactRows);
      }
    }
    const activeAid = meta?.active_artifact_id ?? meta?.activeArtifactId;
    const pendingKind = String(pendingTurnIntentRef.current || '').toLowerCase();
    const inflightTabId = inFlight ? inFlightArtifactPlaceholderId(pendingKind) : null;
    if (!userChoseDeliverableTab) {
      if (inFlight && inflightTabId) {
        setSelectedArtifactId(inflightTabId);
        setRailTab(inflightTabId);
      } else if (activeAid) {
        const aid = String(activeAid);
        setSelectedArtifactId(aid);
        if (mergedArtifactRows.length > 1) {
          setRailTab(aid);
        }
      } else if (mergedArtifactRows.length && !inFlight) {
        const last = mergedArtifactRows[mergedArtifactRows.length - 1].id;
        setSelectedArtifactId(last);
        if (mergedArtifactRows.length > 1) {
          setRailTab(last);
        }
      }
    }
    const cot = meta?.chain_of_thought ?? meta?.chainOfThought;
    if (cot != null && String(cot).trim() !== '') {
      setChainOfThought(String(cot));
    }
    const pa = meta?.prd_available ?? meta?.prdAvailable;
    if (pa) {
      setPrdAvailableHint(true);
      markPrdAvailable();
    }
    if (meta?.intent && !userChoseDeliverableTab) {
      const sticky = resolveStickyChatIntent(
        meta.intent,
        mergedArtifactRows.length ? mergedArtifactRows : sessionArtifactsRef.current,
        stickyOpts
      );
      if (sticky) setChatIntent(sticky);
    }
    const cdt = meta?.confirm_doc_type ?? meta?.confirmDocType;
    if (cdt) setChatConfirmDocType(String(cdt));
    const awaitingAction = meta?.awaiting_action ?? meta?.awaitingAction;
    if (awaitingAction !== undefined && awaitingAction !== null) {
      setChatAwaitingAction(String(awaitingAction));
    } else if (String(meta?.phase || '').toUpperCase() !== 'DONE') {
      setChatAwaitingAction(null);
    }
    const da = meta?.deploy_available ?? meta?.deployAvailable;
    if (da !== undefined && da !== null) setDeployAvailable(Boolean(da));
    const cv = meta?.code_validated ?? meta?.codeValidated;
    if (cv !== undefined && cv !== null) setCodeValidated(Boolean(cv));

    const pml = meta?.platform_mode_locked ?? meta?.platformModeLocked;
    const platformLocked =
      pml != null ? Boolean(pml) : platformModeLocked;
    if (pml != null) setPlatformModeLocked(Boolean(pml));

    const pdcm = meta?.platform_default_connected_mode ?? meta?.platformDefaultConnectedMode;
    if (pdcm != null) {
      applyPlatformConnectedState(String(pdcm));
    } else {
      const usc = meta?.use_studio_context ?? meta?.useStudioContext;
      if (usc != null && !platformLocked) {
        const connected = Boolean(usc);
        setUseStudioContext(connected);
        useStudioContextRef.current = connected;
      }
    }

    const deliverable = mergeDeliverableMeta({
      intent: meta?.intent,
      artifactType: meta?.artifact_type ?? meta?.artifactType,
      artifactUrl: meta?.artifact_url ?? meta?.artifactUrl,
      generatedCode: gc,
      artifactSummary: sanitizedSummary,
      panelBlocks: panels,
    });
    if (!userChoseDeliverableTab) {
      if (deliverable.intent) {
        const sticky = resolveStickyChatIntent(
          deliverable.intent,
          mergedArtifactRows.length ? mergedArtifactRows : sessionArtifactsRef.current,
          stickyOpts
        );
        if (sticky) setChatIntent(sticky);
      }
      if (deliverable.artifactType) setArtifactType(deliverable.artifactType);
      if (deliverable.artifactUrl != null && String(deliverable.artifactUrl).trim()) {
        setArtifactUrl(deliverable.artifactUrl);
      } else if (!inFlight) {
        const kind = String(deliverable.artifactType || deliverable.intent || '').toLowerCase();
        if (kind && kind !== 'static_report') {
          setArtifactUrl(null);
        }
      }
      if (deliverable.panelBlocks?.length) {
        setPanelBlocks(deliverable.panelBlocks);
      }
    }
    const wfStage = meta?.workflow_stage ?? meta?.workflowStage;
    if (wfStage) {
      // Ticket poll / resolve API is authoritative for NUP close. Never let a stale
      // chat checkpoint downgrade DONE (or flip DONE back to escalation/awaiting).
      setWorkflowStage((prev) => {
        const next = String(wfStage);
        const p = String(prev || '').toUpperCase();
        const n = next.toUpperCase();
        if (p === WORKFLOW_STAGE.DONE && n !== WORKFLOW_STAGE.DONE) return prev;
        if (
          p === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED &&
          n === WORKFLOW_STAGE.AWAITING_USER_CONFIRMATION
        ) {
          return prev;
        }
        return next;
      });
    }
    const wfState = meta?.workflow_state ?? meta?.workflowState;
    if (wfState && typeof wfState === 'object') {
      setWorkflowState((prev) => {
        const prevUc = getUserConfirmation({ workflow_state: prev || {} });
        const nextUc = getUserConfirmation({ workflow_state: wfState });
        if (prevUc?.admin_comment && !nextUc?.admin_comment) return prev;
        return wfState;
      });
    }
    const tn =
      meta?.ticket_number ??
      meta?.ticketNumber ??
      (wfState && typeof wfState === 'object'
        ? wfState.ticket_number || wfState.ticketNumber
        : null);
    if (tn) setMirroredTicketNumber(String(tn));

    if (!inFlight && !meta?.generating) {
      setPendingTurnIntent(null);
    }
  }, [markPrdAvailable, applyPlatformConnectedState, platformModeLocked]);

  const fetchThread = useCallback(
    async (sessionId, seedM = null, options = {}) => {
      if (!VIBE_CHAT_API_BASE || !sessionId) return;
      const silent = Boolean(options.silent);
      activeThreadRef.current = sessionId;
      if (!silent) {
        setThreadLoading(true);
        setThreadError(null);
      }
      try {
        const { messages: list, meta, status } = await loadChatSessionThread(
          sessionId,
          VIBE_CHAT_API_BASE
        );
        if (activeThreadRef.current !== sessionId) return;
        let finalList = list;
        if (finalList.length === 0 && seedM?.message) {
          finalList = [{ role: 'assistant', content: seedM.message }];
        }
        setMessages(finalList);
        const mergedMeta = {
          ...meta,
          phase: meta.phase || seedM?.phase,
          awaiting_user:
            meta.awaiting_user !== undefined && meta.awaiting_user !== null
              ? meta.awaiting_user
              : seedM?.awaitingUser,
          generated_code: meta.generated_code ?? seedM?.generatedCode,
          artifact_summary: meta.artifact_summary ?? seedM?.artifactSummary,
          prd_available: meta.prd_available ?? seedM?.prdAvailable,
          deploy_available: meta.deploy_available ?? seedM?.deployAvailable,
          code_validated: meta.code_validated ?? seedM?.codeValidated,
          intent: meta.intent ?? seedM?.intent,
          confirm_doc_type: meta.confirm_doc_type ?? seedM?.confirmDocType,
          artifact_type: meta.artifact_type ?? seedM?.artifactType,
          artifact_url: meta.artifact_url ?? seedM?.artifactUrl,
          chain_of_thought: meta.chain_of_thought ?? meta.chainOfThought ?? seedM?.chainOfThought,
          panel_blocks: meta.panel_blocks ?? seedM?.panelBlocks,
          artifacts: meta.artifacts ?? seedM?.artifacts,
          active_artifact_id: meta.active_artifact_id ?? seedM?.activeArtifactId,
          generating: meta.generating ?? seedM?.generating,
        };
        applyMeta(mergedMeta);
        setChatGenerating(inferGeneratingFromThreadMeta(mergedMeta, status ?? seedM));
      } catch (err) {
        if (activeThreadRef.current !== sessionId) return;
        if (silent) {
          setActionError(formatApiError(err));
          return;
        }
        setThreadError(formatApiError(err));
        setMessages([]);
      } finally {
        if (!silent && activeThreadRef.current === sessionId) {
          setThreadLoading(false);
        }
      }
    },
    [applyMeta]
  );

  const selectSession = useCallback(
    async (id, seedM = null) => {
      if (!id || id === selectedIdRef.current) return;
      // Update ref immediately so re-entrant calls (URL-sync effect) are blocked.
      selectedIdRef.current = id;
      activeThreadRef.current = id;
      setSelectedId(id);
      setThreadLoading(true);
      setThreadError(null);
      setChatPhase('');
      setChatIntent('unclear');
      setPendingTurnIntent(null);
      setChatIntentMeta(null);
      setChatConfirmDocType(null);
      setArtifactType(null);
      setArtifactUrl(null);
      setChatAwaitingUser(false);
      setChatGeneratedCode(null);
      setArtifactSummary(null);
      setPanelBlocks([]);
      setSessionArtifacts([]);
      setSelectedArtifactId(null);
      setWorkflowStage(null);
      setWorkflowState(null);
      setMirroredTicketNumber(null);
      setChatAwaitingAction(null);
      setResolveComment('');
      setResolveSubmitting(false);
      setResolveDialogOpen(false);
      // Keep prior CoT visible until the new session thread returns meta — panel
      // stays mounted whenever selectedId is set (empty string only while loading).
      setChainOfThought('');
      setPrdAvailableHint(false);
      setChatGenerating(false);
      setActionError(null);
      setMessages([]);
      setChatInput('');
      setStartError(null);
      setDeployAvailable(false);
      setCodeValidated(false);
      setDeployMessage(null);
      setChatExcelFile(null);
      threadPinnedToBottomRef.current = true;
      persistActiveChatSessionId(id);
      await fetchThread(id, seedM);
    },
    [fetchThread]
  );

  // Sync selectedId → URL (one-way; keeps URL in step with sidebar clicks).
  useEffect(() => {
    if (selectedId) setSearchParams({ session: selectedId }, { replace: true });
  }, [selectedId, setSearchParams]);

  // Restore the last open session on a bare page load (no ?session= in the
  // URL) so a refresh doesn't lose the active chat.
  useEffect(() => {
    if (urlSessionId || !VIBE_CHAT_API_BASE || sessionsLoading) return;
    const persistedId = readActiveChatSessionIdFromStorage();
    if (!persistedId || selectedIdRef.current === persistedId) return;
    void selectSession(persistedId, null).catch(() => {
      clearActiveChatSessionId();
    });
    // eslint-disable-next-line -- selectSession intentionally excluded, see urlSessionId restore effect above
  }, [urlSessionId, sessionsLoading]);

  useEffect(() => {
    if (!VIBE_CHAT_API_BASE || !selectedId || !chatGenerating) {
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${VIBE_CHAT_API_BASE}/chat/${selectedId}/status`);
        const m = mapChatResponse(data);
        if (cancelled) return;
        applyPrdFlag(m);
        applyMeta({
          phase: m.phase,
          generating: m.generating,
          awaiting_user: m.awaitingUser,
          intent: m.intent,
          intent_meta: m.intentMeta,
          confirm_doc_type: m.confirmDocType,
          generated_code: m.generatedCode,
          artifact_summary: m.artifactSummary,
          panel_blocks: m.panelBlocks,
          artifacts: m.artifacts,
          active_artifact_id: m.activeArtifactId,
          artifact_url: m.artifactUrl,
          artifact_type: m.artifactType,
          chain_of_thought: m.chainOfThought,
          deploy_available: m.deployAvailable,
          code_validated: m.codeValidated,
          workflow_stage: m.workflowStage,
          workflow_state: m.workflowState,
        });
        if (m.confirmDocType) setChatConfirmDocType(m.confirmDocType);
        if (m.platformDefaultConnectedMode != null) {
          applyPlatformConnectedState(m.platformDefaultConnectedMode);
        } else if (!platformModeLocked) {
          setUseStudioContext(Boolean(m.useStudioContext));
          useStudioContextRef.current = Boolean(m.useStudioContext);
        }
        if (m.ticketNumber) setMirroredTicketNumber(String(m.ticketNumber));

        if (m.generating) {
          return;
        }

        await fetchThread(selectedId, m, { silent: true });
        await loadSessions({ soft: true });
        if (!cancelled) {
          setChatGenerating(false);
        }
      } catch (err) {
        if (!cancelled) {
          setActionError(formatApiError(err));
          setChatGenerating(false);
        }
      }
    };
    poll();
    const fastKick = setTimeout(poll, 450);
    const timer = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearTimeout(fastKick);
      clearInterval(timer);
    };
  }, [
    selectedId,
    chatGenerating,
    fetchThread,
    loadSessions,
    applyMeta,
    applyPrdFlag,
    applyPlatformConnectedState,
    platformModeLocked,
  ]);

  // Refetch the active thread when the tab becomes visible again (or after
  // in-app navigation remounts) so generating UI resumes if work is still running.
  useEffect(() => {
    if (!VIBE_CHAT_API_BASE || !selectedId) return undefined;
    const onVisible = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      void fetchThread(selectedId, null, { silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [selectedId, fetchThread]);

  // Email confirmation updates the mirrored Halo ticket outside chat. Poll that
  // ticket while awaiting / escalated / provisioning / unsettled DONE.
  // Stop once Complete is green or an admin resolution is recorded.
  useEffect(() => {
    const tn = String(mirroredTicketNumber || '').trim();
    const stage = String(workflowStage || '').toUpperCase();
    const syncable =
      stage === WORKFLOW_STAGE.AWAITING_USER_CONFIRMATION ||
      stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED ||
      stage === WORKFLOW_STAGE.PROVISIONING ||
      stage === WORKFLOW_STAGE.DONE;
    if (!INCIDENTS_API || !tn || !syncable) {
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const sync = async () => {
      try {
        const { data } = await axios.get(`${INCIDENTS_API}/tickets/${encodeURIComponent(tn)}`);
        if (cancelled || !data) return;
        const nextStage = data.workflow_stage ?? data.workflowStage;
        const nextState = data.workflow_state ?? data.workflowState;
        const nextU = String(nextStage || '').toUpperCase();
        const stageChanged = Boolean(nextStage) && nextU !== stage;
        if (stageChanged) {
          setWorkflowStage(String(nextStage));
        }
        if (nextState && typeof nextState === 'object') {
          setWorkflowState((prev) => {
            try {
              if (JSON.stringify(prev || {}) === JSON.stringify(nextState)) return prev;
            } catch {
              /* fall through */
            }
            return nextState;
          });
          const nextSteps = Array.isArray(nextState.steps) ? nextState.steps : [];
          const nextCompleteDone = nextSteps.some(
            (s) =>
              s &&
              String(s.key || '') === 'complete' &&
              String(s.status || '').toLowerCase() === 'done',
          );
          const nextAdminResolved = Boolean(
            String(getUserConfirmation({ workflow_state: nextState })?.admin_comment || '').trim(),
          );
          if (
            nextU === WORKFLOW_STAGE.DONE &&
            (nextCompleteDone || nextAdminResolved) &&
            timer
          ) {
            clearInterval(timer);
            timer = null;
          }
        }
        // Soft-append ISSUE message only — never refetch thread on DONE (stale
        // chat checkpoint was flip-flopping stage and spamming Loading thread…).
        if (stageChanged && selectedId && nextU === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED) {
          try {
            await fetchThread(selectedId, null, { silent: true });
          } catch {
            /* best-effort */
          }
        }
      } catch {
        /* best-effort — leave chat meta as-is */
      }
    };
    // If we already have a settled admin resolution locally, don't start polling.
    const localUc = getUserConfirmation({
      workflow_stage: workflowStage,
      workflow_state: workflowState || {},
    });
    if (
      stage === WORKFLOW_STAGE.DONE &&
      String(localUc?.admin_comment || '').trim()
    ) {
      return undefined;
    }
    sync();
    timer = setInterval(sync, 5000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
    // workflowState intentionally omitted from deps — reading it only for the
    // one-shot "already settled" guard; including it would restart the poller.
    // eslint-disable-next-line -- workflowState omitted on purpose (see above)
  }, [mirroredTicketNumber, workflowStage, selectedId, fetchThread]);

  // Load session from URL on initial mount / external navigation.
  // Intentionally omits selectSession from deps — selectSession is stable
  // (depends only on fetchThread) so this is safe, and including it would
  // re-run this effect on every selectedId change, causing a reset loop.
  useEffect(() => {
    if (!urlSessionId || !VIBE_CHAT_API_BASE || sessionsLoading) return;
    if (selectedIdRef.current === urlSessionId) return;
    void selectSession(urlSessionId, null);
    // eslint-disable-next-line -- selectSession is intentionally excluded to avoid reset loop
  }, [urlSessionId, sessionsLoading]);

  const handleResolveAccessIssue = async () => {
    const tn = String(mirroredTicketNumber || '').trim();
    const comment = resolveComment.trim();
    if (!INCIDENTS_API || !tn || resolveSubmitting || !canResolveAccessIssue) return;
    if (!comment) {
      toast.error('Add a short comment describing how the SAP login issue was resolved.');
      return;
    }
    setResolveSubmitting(true);
    setActionError(null);
    try {
      const { data } = await axios.post(
        `${INCIDENTS_API}/tickets/${encodeURIComponent(tn)}/resolve-access-issue`,
        {
          resolved_by: resolverIdentity,
          comment,
        }
      );
      const next = data?.ticket;
      if (next) {
        const nextStage = next.workflow_stage ?? next.workflowStage;
        const nextState = next.workflow_state ?? next.workflowState;
        if (nextStage) setWorkflowStage(String(nextStage));
        if (nextState && typeof nextState === 'object') setWorkflowState(nextState);
      } else {
        const note = resolverIdentity
          ? `${comment}\n\nResolved by: ${resolverIdentity}`
          : comment;
        setWorkflowStage(WORKFLOW_STAGE.DONE);
        setWorkflowState((prev) => ({
          ...(prev || {}),
          user_confirmation: {
            ...((prev && prev.user_confirmation) || {}),
            admin_comment: note,
            admin_resolved_by: resolverIdentity,
            admin_resolved_at: new Date().toISOString(),
          },
        }));
      }
      setResolveComment('');
      setResolveDialogOpen(false);
      toast.success(
        data?.servicenow_resolved
          ? 'Marked resolved — Halo completed and ServiceNow resolved.'
          : 'Marked resolved — Halo ticket completed.'
      );
      // Do not fetchThread here — chat checkpoint may still say ACCESS_ISSUE and
      // would fight the ticket DONE state (caused continuous Loading thread…).
    } catch (err) {
      const msg = formatApiError(err) || 'Could not mark the SAP login issue as resolved.';
      setActionError(msg);
      toast.error(msg);
    } finally {
      setResolveSubmitting(false);
    }
  };

  const startNewChat = async () => {
    if (!canCreateChat || !VIBE_CHAT_API_BASE || chatStarting) return;
    setStartError(null);
    setChatStarting(true);
    try {
      const { data } = await axios.post(`${VIBE_CHAT_API_BASE}/chat/start`, {
        use_studio_context: isPlatformConnected || useStudioContextRef.current,
      });
      const m = mapChatResponse(data);
      if (!m.sessionId) {
        setStartError('Chat start did not return a session id.');
        return;
      }
      const welcomeMsg = m.message && !isProcessingPlaceholder(m.message) ? m.message : STUDIO_WELCOME_MESSAGE;
      const newSession = { session_id: m.sessionId, phase: m.phase, preview: welcomeMsg };
      setSessions((prev) => [newSession, ...prev]);
      // Select the new session and seed its thread with the welcome message
      selectedIdRef.current = null; // force selectSession to proceed
      await selectSession(m.sessionId, { message: welcomeMsg, phase: m.phase, awaitingUser: m.awaitingUser });
      applyMeta({
        platform_default_connected_mode: m.platformDefaultConnectedMode,
        use_studio_context: m.useStudioContext,
      });
      if (m.generating) setChatGenerating(true);
      await loadSessions();
      setTimeout(() => chatInputRef.current?.focus(), 0);
    } catch (err) {
      setStartError(formatApiError(err));
    } finally {
      setChatStarting(false);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const sendReply = async () => {
    const text = chatInput.trim();
    if (
      !canSendMessage ||
      !VIBE_CHAT_API_BASE ||
      !selectedId ||
      !text ||
      chatSending ||
      chatGenerating
    ) {
      return;
    }
    setChatSending(true);
    setActionError(null);
    setChatInput('');
    const attachedFile = chatExcelFile;
    setChatExcelFile(null);
    if (chatFileInputRef.current) chatFileInputRef.current.value = '';
    threadPinnedToBottomRef.current = true;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    const guessedIntent = inferIntentFromUserMessage(text);
    if (guessedIntent !== 'unclear') {
      setPendingTurnIntent(guessedIntent);
      setChatIntent(guessedIntent);
      setArtifactType(guessedIntent);
      const inflightId = inFlightArtifactPlaceholderId(guessedIntent);
      if (inflightId) {
        setSelectedArtifactId(inflightId);
        setRailTab(inflightId);
      }
    }
    setChatGenerating(true);
    try {
      const body = {
        text,
        use_studio_context: isPlatformConnected || useStudioContextRef.current,
      };
      const modePref = parseConnectedPreference(text);
      if (modePref !== null) {
        body.use_studio_context = modePref;
      }
      if (attachedFile) {
        body.excel_spec_b64 = await fileToBase64(attachedFile);
      }
      const { data } = await axios.post(`${VIBE_CHAT_API_BASE}/chat/${selectedId}/message`, body);
      const m = mapChatResponse(data);
      applyMeta({
        ...m,
        intent: m.intent,
        intent_meta: m.intentMeta,
        artifact_type: m.artifactType,
        platform_default_connected_mode: m.platformDefaultConnectedMode,
        use_studio_context: m.useStudioContext,
        platform_mode_locked: m.platformModeLocked,
      });
      const stillGenerating = Boolean(m.generating);
      setChatGenerating(stillGenerating);
      if (!stillGenerating) {
        await fetchThread(selectedId, m, { silent: true });
      }
    } catch (err) {
      setActionError(formatApiError(err));
      setChatGenerating(false);
    } finally {
      setChatSending(false);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendReply();
    }
  };

  const handleDeployChatCode = async () => {
    if (!canDeployChat || !selectedId || !deployAvailable || deploying) return;
    setActionError(null);
    setDeployMessage(null);
    setDeploying(true);
    try {
      const result = await deployChatStudioCode(selectedId);
      const msg = result.message || (result.success ? 'Deployed to SAP.' : 'Deploy failed.');
      setDeployMessage(msg);
      if (!result.success) {
        setActionError(msg);
      }
    } catch (err) {
      setActionError(formatApiError(err));
    } finally {
      setDeploying(false);
    }
  };

  const copyDeliverable = async () => {
    const payload =
      deliverableKind === 'integration_suite'
        ? integrationSuiteCopyPayload(railPanelBlocks, railArtifactSummary)
        : deliverableKind === 'workflow' || deliverableKind === 'security'
          ? String(railArtifactSummary || railGeneratedCode || '').trim()
          : resolveCopyPayload(deliverableKind, railGeneratedCode, resolvedArtifactUrl);
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError('Could not copy');
    }
  };

  const railSummaryForPanel = sanitizeRailArtifactSummary(railArtifactSummary, {
    intent: effectiveIntent,
    artifactType: panelArtifactType,
  });
  const hasDeliverableContent = hasRailDeliverableContent({
    deliverableKind,
    intent: effectiveIntent,
    artifactType: panelArtifactType,
    generatedCode: railGeneratedCode,
    artifactSummary: railSummaryForPanel,
    panelBlocks: railPanelBlocks,
    artifactUrl: resolvedArtifactUrl,
    workflowStage: railWorkflowStage,
  });
  const phaseUp = String(chatPhase).toUpperCase();
  const hasPersistedRailArtifacts = contentfulArtifacts(sessionArtifacts).length > 0;
  const hasRailTabs = railArtifactPills.length > 0;
  const pinnedSessionRailContent = useMemo(
    () =>
      sessionArtifacts.some((row) => artifactHasContent(row)) ||
      panelBlocksRef.current.length > 0,
    [sessionArtifacts, panelBlocks]
  );
  const railContentVisible =
    hasDeliverableContent ||
    hasPersistedRailArtifacts ||
    pinnedSessionRailContent ||
    (chatGenerating && hasRailTabs);
  const railShowsFinalOutput =
    railContentVisible &&
    (phaseUp === 'DONE' ||
      phaseUp === 'REVISE' ||
      (hasPersistedRailArtifacts && (phaseUp === 'GENERATE' || chatGenerating)) ||
      (deliverableKind === 'workflow' && phaseUp !== 'GATHER'));
  const showDeliverablePanel = railContentVisible && railShowsFinalOutput;

  const visibleThreadMessages = useMemo(
    () =>
      messages.filter((m) => {
        if (isHiddenChatMessage(m)) return false;
        if (m.role === 'assistant' && (chatGenerating || chatSending)) {
          if (
            shouldHideAssistantWhileGenerating(m.content, {
              intent: effectiveIntent,
              artifactType: panelArtifactType,
            })
          ) {
            return false;
          }
        }
        return true;
      }),
    [messages, chatGenerating, chatSending, effectiveIntent, panelArtifactType]
  );

  const deliverablePanelUpdating =
    showDeliverablePanel && !chatFinished && chatGenerating && panelFollowsInFlightTurn;
  const showPrdArtifact =
    Boolean(selectedId) &&
    shouldShowPrdRailTab({
      phase: chatPhase,
      sessionSupportsPrd,
      prdAvailable,
      prdAvailableHint,
    });

  const workflowTicketMeta = useMemo(
    () => ({ workflow_stage: railWorkflowStage, workflow_state: railWorkflowState || {} }),
    [railWorkflowStage, railWorkflowState]
  );
  const workflowSteps = useMemo(() => {
    if (deliverableKind !== 'workflow') return [];
    const steps = getWorkflowSteps(workflowTicketMeta);
    if (steps.length > 0) return steps;
    // getWorkflowSteps() already falls back per-type internally; this is a last-resort
    // safety net keyed off the same workflow-type catalog, not a hardcoded NUP default.
    const type = resolveWorkflowType(workflowTicketMeta);
    if (type.id === WORKFLOW_TYPE_TER) return defaultTerSteps();
    if (type.id === WORKFLOW_TYPE_PWD_RESET) return defaultPwdResetSteps();
    return defaultNupSteps();
  }, [deliverableKind, workflowTicketMeta, workflowState]);
  const showWorkflowBanner =
    Boolean(selectedId) && !threadLoading && deliverableKind === 'workflow';
  const workflowStageLabel =
    deliverableKind === 'workflow'
      ? getWorkflowStageLabel({
          workflow_stage: workflowStage,
          workflow_state: workflowState || {},
        })
      : null;
  const userConfirmation = getUserConfirmation(workflowTicketMeta);
  const adminResolutionComment = String(userConfirmation?.admin_comment || '').trim();
  const adminResolvedBy = String(userConfirmation?.admin_resolved_by || '').trim();
  const adminResolvedAt = userConfirmation?.admin_resolved_at;
  const showAdminResolution =
    showWorkflowBanner &&
    String(workflowStage || '').toUpperCase() === WORKFLOW_STAGE.DONE &&
    Boolean(adminResolutionComment);
  const showAccessIssueResolve =
    showWorkflowBanner &&
    String(workflowStage || '').toUpperCase() === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED;

  const showValidationHint =
    deliverableKind === 'abap' &&
    Boolean(String(railGeneratedCode || '').trim()) &&
    String(chatPhase).toUpperCase() === 'DONE' &&
    !deployAvailable &&
    !chatGenerating;

  const codeTabLabel =
    deliverableKind === 'workspace'
      ? 'Workspace'
      : deliverableKind === 'abap'
        ? 'Generated ABAP'
        : deliverableKind === 'static_report'
          ? 'Static report'
          : deliverableKind === 'security'
            ? 'Security results'
            : deliverableKind === 'workflow'
              ? 'Workflow'
              : deliverableKind === 'integration_suite'
                ? 'Integration Suite'
                : deliverableKind === 'pending'
                  ? 'Loading…'
                  : 'Output';

  // Reasoning is for side-rail deliverables without a progress graph.
  // NUP/workflow already has WorkflowTaskGraph — hide Reasoning there.
  const showReasoning =
    Boolean(selectedId) &&
    !threadLoading &&
    ['abap', 'security', 'static_report', 'integration_suite'].includes(deliverableKind);

  const chainSteps = useMemo(
    () =>
      String(chainOfThought || '')
        .split(/\n\n+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .reverse(), // newest first — same as tickets Chain of Thought
    [chainOfThought]
  );

  useEffect(() => {
    if (!showReasoning) setChainExpanded(false);
  }, [showReasoning]);

  useEffect(() => {
    writeLocalBool(LS_SESSIONS_OPEN, sidebarOpen);
  }, [sidebarOpen]);

  useEffect(() => {
    writeLocalBool(LS_RAIL_OPEN, railOpen);
  }, [railOpen]);

  useEffect(() => {
    writeLocalNum(LS_RAIL_WIDTH, railWidthPx);
  }, [railWidthPx]);

  // After a new deliverable lands, focus its tab once — do not override manual tab picks on poll.
  useEffect(() => {
    if (chatGenerating || !selectedId) return;
    const phase = String(chatPhase || '').toUpperCase();
    if (phase !== 'DONE' && phase !== 'REVISE') return;

    const count = railArtifactPills.length;
    const hasDeliverable =
      count > 0 ||
      Boolean(String(chatGeneratedCode || '').trim()) ||
      Boolean(String(resolvedArtifactUrl || '').trim()) ||
      (Array.isArray(railPanelBlocks) && railPanelBlocks.length > 0);

    if (!hasDeliverable) return;

    const focus = deliverableRailFocusRef.current;
    if (focus.sessionId === selectedId && count <= focus.artifactCount) {
      return;
    }
    deliverableRailFocusRef.current = { sessionId: selectedId, artifactCount: count };

    const manual = manualArtifactTabRef.current;
    if (manual.sessionId === selectedId && manual.artifactId) {
      return;
    }
    if (count > 1) {
      const latest = railArtifactPills[count - 1]?.id;
      if (latest) {
        setSelectedArtifactId(latest);
        setRailTab(latest);
      }
    } else if (count === 1) {
      const only = railArtifactPills[0]?.id;
      if (only) {
        setSelectedArtifactId(only);
        setRailTab(only);
      } else {
        setRailTab('code');
      }
    } else {
      setRailTab('code');
    }
    setRailOpen(true);
  }, [selectedId, chatPhase, chatGenerating, railArtifactPills, chatGeneratedCode, resolvedArtifactUrl, railPanelBlocks]);

  // During requirements confirm, surface PRD when available.
  useEffect(() => {
    const phase = String(chatPhase || '').toUpperCase();
    if (phase === 'CONFIRM' && showPrdArtifact) {
      setRailTab('prd');
    }
  }, [chatPhase, showPrdArtifact]);

  useEffect(() => {
    setChainExpanded(false);
    deliverableRailFocusRef.current = { sessionId: selectedId, artifactCount: 0 };
    manualArtifactTabRef.current = { sessionId: null, artifactId: null };
  }, [selectedId]);

  const startRailResize = useCallback(
    (e) => {
      if (!railOpen) return;
      e.preventDefault();
      const startX = e.clientX;
      const startW =
        railPanelRef.current?.getBoundingClientRect()?.width ||
        railWidthPx ||
        300;
      resizeDragRef.current = { startX, startW };
      setRailResizing(true);

      const onMove = (ev) => {
        const delta = resizeDragRef.current.startX - ev.clientX;
        const next = Math.max(280, Math.min(640, resizeDragRef.current.startW + delta));
        setRailWidthPx(next);
      };
      const onUp = () => {
        setRailResizing(false);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [railOpen, railWidthPx]
  );

  const sessionsWidthStyle = sidebarOpen
    ? { flex: '0 0 clamp(190px, 16vw, 260px)', width: 'clamp(190px, 16vw, 260px)' }
    : { flex: '0 0 44px', width: 44 };

  const railWidthStyle = !railOpen
    ? { flex: '0 0 44px', width: 44 }
    : railWidthPx != null
      ? { flex: `0 0 ${railWidthPx}px`, width: railWidthPx }
      : { flex: '0 0 clamp(320px, 28vw, 480px)', width: 'clamp(320px, 28vw, 480px)' };

  return (
    <div
      className="vibe-chat-workspace -mx-6 -mt-2 flex min-h-0 flex-col gap-0 overflow-hidden max-lg:h-auto max-lg:max-h-none max-lg:overflow-visible lg:h-[calc(100vh-6.5rem)] lg:max-h-[calc(100vh-6.5rem)] sm:-mx-6"
      data-testid="vibe-chat-workspace"
    >
      <style>{`
        @keyframes vibeWsShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes vibeWsMsgIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .vibe-ws-hero-shimmer {
          background: linear-gradient(
            110deg,
            rgba(255,255,255,0) 0%,
            rgba(255,255,255,0.55) 45%,
            rgba(255,255,255,0) 90%
          );
          background-size: 200% 100%;
          animation: vibeWsShimmer 4s ease-in-out infinite;
        }
        @keyframes vibeWsGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.25); }
          50% { box-shadow: 0 0 24px 2px rgba(139, 92, 246, 0.2); }
        }
        .vibe-ws-selected {
          animation: vibeWsGlow 2.5s ease-in-out infinite;
        }
      `}</style>

      {/* Hero */}
      <div className="relative shrink-0 overflow-hidden border-b border-cp-line bg-cp-grad px-4 py-5 text-white sm:px-6">
        <div className="pointer-events-none absolute inset-0 vibe-ws-hero-shimmer opacity-30" aria-hidden />
        <div className="pointer-events-none absolute -left-20 top-0 h-64 w-64 rounded-full bg-cp-inset/20 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -right-10 bottom-0 h-48 w-48 rounded-full bg-cp-indigo/20 blur-3xl" aria-hidden />
        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="border-0 bg-[color-mix(in_srgb,white_10%,transparent)] text-white hover:bg-[color-mix(in_srgb,white_20%,transparent)] lg:hidden"
              onClick={() => setSidebarOpen((o) => !o)}
            >
              <PanelLeft className="mr-2 h-4 w-4" />
              Sessions
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-[color-mix(in_srgb,white_10%,transparent)] p-2 ring-1 ring-white/20 backdrop-blur-sm">
              <Code2 className="h-6 w-6 text-cp-mod" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight sm:text-2xl">Vibe Coding Studio</h1>
              <p className="text-xs text-white/80 sm:text-sm">
                Your sessions — select a past chat or start a new one
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="flex items-center gap-3 rounded-lg border border-white/15 bg-[color-mix(in_srgb,white_10%,transparent)] px-3 py-2 backdrop-blur-sm"
              data-testid="studio-connection-status"
              title={
                !platformModeReady
                  ? 'Loading organization chat mode…'
                  : isPlatformConnected
                    ? 'Connected — SAP context enabled for this organization'
                    : 'Disconnected — ABAP-only mode'
              }
            >
              <span className="relative flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                {!platformModeReady ? (
                  <Loader2 className="h-3 w-3 animate-spin text-cp-muted" aria-hidden />
                ) : isPlatformConnected ? (
                  <>
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cp-ok opacity-40" aria-hidden />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-cp-ok shadow-[0_0_10px_2px_rgba(16,185,129,0.65)]" aria-hidden />
                  </>
                ) : (
                  <span className="inline-flex h-3 w-3 rounded-full border border-cp-line bg-cp-faint/40" aria-hidden />
                )}
              </span>
              <div className="text-xs leading-snug text-white">
                <span className="font-medium">
                  {!platformModeReady ? 'Checking mode…' : isPlatformConnected ? 'Connected' : 'Disconnected'}
                </span>
                <span className="block text-[11px] font-normal text-white/70">
                  {!platformModeReady
                    ? 'Reading administrator chat settings'
                    : isPlatformConnected
                      ? 'SAP context enabled by your organization'
                      : 'No SAP context — set by your administrator'}
                </span>
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={loadSessions}
              disabled={sessionsLoading || !VIBE_CHAT_API_BASE}
              variant="secondary"
              className="border-0 bg-[color-mix(in_srgb,white_15%,transparent)] text-white hover:bg-[color-mix(in_srgb,white_25%,transparent)]"
            >
              <RefreshCw className={cn('mr-2 h-4 w-4', sessionsLoading && 'animate-spin')} />
              Refresh
            </Button>
            <span title={permissionDeniedTitle(canCreateChat, 'start a new chat')}>
              <Button
                type="button"
                size="sm"
                onClick={startNewChat}
                disabled={!canCreateChat || chatStarting || !VIBE_CHAT_API_BASE}
                className="bg-gradient-to-r from-cp-mod to-cp-imp font-semibold text-cp-fg shadow-lg hover:opacity-95"
                data-testid="unified-new-chat"
              >
                {chatStarting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <MessageSquarePlus className="mr-2 h-4 w-4" />
                )}
                New chat
              </Button>
            </span>
          </div>
        </div>
      </div>

      {!VIBE_CHAT_API_BASE && (
        <div className="px-4 pt-4 sm:px-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Chat unavailable</AlertTitle>
            <AlertDescription>
              Chat history is not available in this environment. Contact your administrator if this persists.
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div
        className="flex flex-1 min-h-0 gap-4 overflow-x-auto px-4 py-4 sm:px-5"
        data-testid="vibe-studio-workspace-row"
      >
        {/* Sessions panel */}
        <aside
          className={cn(
            'flex min-h-0 shrink-0 flex-col overflow-hidden rounded-xl border border-cp-line bg-cp-surface',
            !railResizing && 'transition-[width,flex-basis] duration-200 ease-out'
          )}
          style={sessionsWidthStyle}
          data-testid="vibe-studio-sessions"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-cp-line px-3 py-2.5">
            {sidebarOpen ? (
              <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-cp-fg">
                <Activity className="h-4 w-4 shrink-0 text-cp-violet" />
                <span>Sessions</span>
                <Badge variant="outline" className="border-cp-line bg-cp-inset text-[10px]">
                  {sessions.length}
                </Badge>
              </div>
            ) : (
              <span className="sr-only">Sessions</span>
            )}
            <div className="flex shrink-0 items-center gap-1">
              {sidebarOpen && (
                <span title={permissionDeniedTitle(canCreateChat, 'start a new chat')}>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-cp-violet hover:bg-cp-inset"
                    onClick={startNewChat}
                    disabled={!canCreateChat || chatStarting || !VIBE_CHAT_API_BASE}
                    title={permissionDeniedTitle(canCreateChat, 'start a new chat') || 'New chat'}
                  >
                    {chatStarting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <MessageSquarePlus className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </span>
              )}
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-cp-muted hover:bg-cp-inset"
                onClick={() => setSidebarOpen((o) => !o)}
                title={sidebarOpen ? 'Collapse sessions' : 'Expand sessions'}
              >
                {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          {sidebarOpen && (
            <div ref={sessionsScrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="space-y-1.5 p-2">
              <form
                className="flex gap-1 pb-1"
                onSubmit={(e) => {
                  e.preventDefault();
                  setSessionQuery((sessionSearchInput || '').trim());
                }}
              >
                <Input
                  value={sessionSearchInput}
                  onChange={(e) => setSessionSearchInput(e.target.value)}
                  placeholder="Search title (contains)…"
                  className="h-8 text-[11px]"
                  data-testid="vibe-sessions-search"
                />
                <Button type="submit" size="sm" variant="secondary" className="h-8 px-2 text-[11px]">
                  Go
                </Button>
              </form>
              {sessionsLoading && (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-cp-muted">
                  <Loader2 className="h-8 w-8 animate-spin text-cp-violet" />
                  <span className="text-xs">Loading…</span>
                </div>
              )}
              {sessionsError && !sessionsLoading && (
                <Alert variant="destructive" className="text-xs">
                  <AlertDescription className="font-mono">{sessionsError}</AlertDescription>
                </Alert>
              )}
              {!sessionsLoading &&
                !sessionsError &&
                sessions.map((row, idx) => {
                  const id = sessionIdOf(row);
                  const active = id && id === selectedId;
                  const ph = phaseOf(row);
                  const dt = dateOf(row);
                  const displayTitle = titleOf(row) || previewOf(row) || 'Untitled session';
                  const isRenaming = id && renamingSessionId === id;
                  // Only animate on first mount; skip re-animation when deselected.
                  const hasAnimated = animatedSessionsRef.current.has(id);
                  if (id && !hasAnimated) animatedSessionsRef.current.add(id);
                  return (
                    <div
                      key={id || `s-${idx}`}
                      className={cn(
                        'group w-full rounded-xl border px-3 py-2.5 text-left transition-all duration-300',
                        active
                          ? 'vibe-ws-selected border-cp-violet bg-cp-surface shadow-md ring-2 ring-cp-violet/50 !opacity-100'
                          : 'border-transparent bg-cp-inset opacity-100 hover:border-cp-line hover:bg-cp-surface hover:shadow-sm'
                      )}
                      style={
                        active || hasAnimated
                          ? undefined
                          : {
                              animation: 'vibeWsMsgIn 0.4s ease both',
                              animationDelay: `${Math.min(idx, 12) * 35}ms`,
                            }
                      }
                    >
                      {isRenaming ? (
                        <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                          <Input
                            value={renameDraft}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            maxLength={80}
                            autoFocus
                            disabled={renameSaving}
                            className="h-8 text-[12px]"
                            data-testid={`rename-session-input-${idx}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void saveRenameSession(id);
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelRenameSession();
                              }
                            }}
                          />
                          {renameError && (
                            <p className="text-[10px] text-destructive">{renameError}</p>
                          )}
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              size="sm"
                              className="h-7 px-2 text-[11px]"
                              disabled={renameSaving}
                              onClick={() => void saveRenameSession(id)}
                              data-testid={`rename-session-save-${idx}`}
                            >
                              {renameSaving ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Check className="h-3 w-3" />
                              )}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-[11px]"
                              disabled={renameSaving}
                              onClick={cancelRenameSession}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => id && void selectSession(id, null)}
                          disabled={!id}
                          className="w-full text-left"
                          aria-current={active ? 'true' : undefined}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <span
                              className="truncate text-[12px] font-medium text-cp-fg"
                              title={displayTitle}
                            >
                              {displayTitle}
                            </span>
                            <div className="flex shrink-0 items-center gap-1">
                              {canRenameChat && id ? (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  title={permissionDeniedTitle(canRenameChat, 'rename this chat') || 'Rename chat'}
                                  className="rounded p-0.5 text-cp-muted opacity-0 transition-opacity hover:bg-cp-inset hover:text-cp-fg group-hover:opacity-100 focus:opacity-100"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    beginRenameSession(row);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      beginRenameSession(row);
                                    }
                                  }}
                                  data-testid={`rename-session-${idx}`}
                                >
                                  <Pencil className="h-3 w-3" />
                                </span>
                              ) : null}
                              {ph && (
                                <Badge variant="outline" className="h-5 shrink-0 border-cp-line px-1.5 text-[9px]">
                                  {ph}
                                </Badge>
                              )}
                            </div>
                          </div>
                          {dt && <div className="mt-1 text-[10px] text-muted-foreground">{dt.toLocaleString()}</div>}
                        </button>
                      )}
                    </div>
                  );
                })}
              {!sessionsLoading && !sessionsError && sessions.length === 0 && (
                <p className="px-2 py-8 text-center text-xs text-muted-foreground" data-testid="vibe-sessions-empty-state">
                  {sessionQuery
                    ? 'No sessions match this search.'
                    : 'No sessions yet. Start a new chat.'}
                </p>
              )}
              {!sessionsLoading && !sessionsError && sessionsHasMore && (
                <div
                  ref={sessionsLoadMoreSentinelRef}
                  className="flex justify-center py-3"
                  data-testid="vibe-sessions-load-more-sentinel"
                >
                  {sessionsLoadingMore ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                      Loading more…
                    </span>
                  ) : (
                    <span className="h-px w-px" aria-hidden />
                  )}
                </div>
              )}
              {!sessionsLoading && !sessionsError && sessions.length > 0 && !sessionsHasMore && !sessionsLoadingMore && (
                <p
                  className="px-2 py-3 text-center text-[11px] text-muted-foreground"
                  data-testid="vibe-sessions-end-of-list"
                >
                  You’ve reached the end
                </p>
              )}
            </div>
          </div>
          )}
        </aside>

        {/* Main chat column */}
        <main className="relative flex min-h-0 min-w-[360px] flex-[1_1_420px] flex-col overflow-hidden rounded-xl border border-cp-line bg-cp-surface">
          <div className="shrink-0 border-b border-cp-line px-4 py-3 sm:px-5">
            {needsConnectedMode && (
              <Alert className="mb-2 border-cp-mod bg-cp-inset/90">
                <AlertCircle className="h-4 w-4 text-cp-mod" />
                <AlertTitle className="text-sm text-cp-mod">Connected mode required</AlertTitle>
                <AlertDescription className="text-xs text-cp-mod/90">
                  {deliverableLabelFromMeta(intentUi)} needs SAP context, which is not enabled for your
                  organization. Please contact your administrator to enable Connected mode.
                </AlertDescription>
              </Alert>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
              {chatPhase && (
                <Badge className={cn('text-xs font-medium', phaseVisual.badgeClass)} variant="outline">
                  {phaseVisual.headline}
                </Badge>
              )}
              {isPlatformConnected && (
                <Badge
                  className="gap-1.5 border-cp-ok bg-cp-inset text-[10px] text-cp-ok"
                  variant="outline"
                >
                  <span className="inline-flex h-2 w-2 rounded-full bg-cp-ok shadow-[0_0_6px_1px_rgba(16,185,129,0.5)]" />
                  Connected
                </Badge>
              )}
              {codeValidated && (
                <Badge className="border-cp-ok bg-cp-inset text-[10px] text-cp-ok" variant="outline">
                  SAP validated
                </Badge>
              )}
              {showAccessIssueResolve && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1.5 border-cp-crit bg-[color-mix(in_srgb,var(--crit)_12%,var(--surface))] px-2 text-[10px] font-semibold text-cp-crit hover:bg-[color-mix(in_srgb,var(--crit)_18%,var(--surface))]"
                  onClick={() => setResolveDialogOpen(true)}
                  title="Human escalation — resolve SAP login issue"
                  data-testid="chat-human-escalation-open"
                >
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Escalation
                </Button>
              )}
              {selectedId &&
                (chatAwaitingUser ? (
                  <Badge className="border-cp-indigo bg-cp-inset text-[10px] text-cp-fg" variant="outline">
                    Your turn
                  </Badge>
                ) : !chatFinished ? (
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                    In progress
                  </Badge>
                ) : null)}
              </div>
              {showReasoning ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className={cn(
                    'h-8 gap-1.5 rounded-full border px-3 text-xs font-semibold',
                    chainExpanded
                      ? 'border-cp-violet/40 bg-cp-violet/15 text-cp-violet'
                      : 'border-cp-line bg-cp-inset text-cp-violet hover:bg-cp-violet/10'
                  )}
                  onClick={() => setChainExpanded((o) => !o)}
                  data-testid="studio-reasoning-toggle"
                >
                  <Brain className="h-3.5 w-3.5" />
                  Reasoning
                  {chainSteps.length > 0 && (
                    <span className="rounded-md bg-cp-surface px-1.5 text-[10px] text-cp-muted">
                      {chainSteps.length}
                    </span>
                  )}
                </Button>
              ) : null}
            </div>

            {showAdminResolution && (
              <div
                className="mt-2 rounded-lg border border-cp-ok/60 bg-[color-mix(in_srgb,var(--ok)_8%,var(--surface))] px-2.5 py-1.5"
                data-testid="chat-admin-resolution-note"
              >
                <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-cp-ok">
                  <span className="inline-flex items-center gap-1 font-semibold">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                    Human resolution recorded
                  </span>
                  <span className="text-muted-foreground">
                    {[
                      adminResolvedBy,
                      adminResolvedAt ? new Date(adminResolvedAt).toLocaleString() : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </p>
                <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-[11px] text-cp-fg">
                  {adminResolutionComment}
                </p>
              </div>
            )}

            <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
              <DialogContent
                className="max-w-md border-cp-crit/40 bg-cp-surface"
                data-testid="chat-human-escalation-dialog"
              >
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-cp-crit">
                    <ShieldAlert className="h-5 w-5" />
                    Human escalation
                  </DialogTitle>
                  <DialogDescription>
                    User had an issue logging in to SAP. Enter a resolution comment and mark
                    resolved. Do not resolve this in chat.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-1.5">
                  <Label htmlFor="vibe-resolve-comment" className="text-xs text-cp-crit">
                    Resolution comment
                  </Label>
                  <Textarea
                    id="vibe-resolve-comment"
                    value={resolveComment}
                    onChange={(e) => setResolveComment(e.target.value)}
                    placeholder="How was the SAP login issue fixed? (required)"
                    className="min-h-[96px] bg-cp-inset text-sm"
                    disabled={!canResolveAccessIssue || resolveSubmitting}
                    data-testid="chat-resolve-access-issue-comment"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setResolveDialogOpen(false)}
                    disabled={resolveSubmitting}
                  >
                    Cancel
                  </Button>
                  <span
                    title={permissionDeniedTitle(
                      canResolveAccessIssue,
                      'mark access issue resolved'
                    )}
                  >
                    <Button
                      type="button"
                      onClick={() => void handleResolveAccessIssue()}
                      disabled={
                        resolveSubmitting ||
                        !canResolveAccessIssue ||
                        !resolveComment.trim() ||
                        !mirroredTicketNumber
                      }
                      className="bg-cp-ok hover:bg-cp-ok"
                      data-testid="chat-resolve-access-issue-btn"
                    >
                      {resolveSubmitting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                      )}
                      Mark SAP login resolved
                    </Button>
                  </span>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {showWorkflowBanner && (
              <div className="mt-3 space-y-3">
                <div
                  className="overflow-hidden rounded-xl border border-cp-line bg-cp-inset/40 shadow-sm"
                  data-testid="chat-workflow-banner"
                >
                  <button
                    type="button"
                    className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left hover:bg-cp-inset/60"
                    onClick={() => setWorkflowBannerOpen((o) => !o)}
                    aria-expanded={workflowBannerOpen}
                    data-testid="chat-workflow-banner-toggle"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <p className="text-sm font-semibold text-cp-fg">Workflow Progress</p>
                      {workflowStageLabel && (
                        <Badge
                          className={
                            workflowStage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED
                              ? 'border-cp-crit bg-[color-mix(in_srgb,var(--crit)_14%,var(--surface))] text-[10px] text-cp-crit'
                              : 'border-cp-mod bg-cp-surface text-[10px] text-cp-mod'
                          }
                          variant="outline"
                        >
                          {workflowStageLabel}
                        </Badge>
                      )}
                    </div>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 shrink-0 text-cp-muted transition-transform duration-200',
                        workflowBannerOpen && 'rotate-180'
                      )}
                    />
                  </button>
                  {workflowBannerOpen && (
                    <div className="border-t border-cp-line px-2 py-2 sm:px-3">
                      <WorkflowTaskGraph
                        steps={workflowSteps}
                        currentStep={(workflowState || {}).current_step}
                        height={200}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {threadError && (
            <div className="px-4 pt-3 sm:px-5">
              <Alert variant="destructive">
                <AlertTitle className="text-sm">Thread</AlertTitle>
                <AlertDescription className="font-mono text-xs">{threadError}</AlertDescription>
              </Alert>
            </div>
          )}
          {actionError && (
            <div className="px-4 pt-3 sm:px-5">
              <Alert variant="destructive">
                <AlertTitle className="text-sm">Action</AlertTitle>
                <AlertDescription className="font-mono text-xs">{actionError}</AlertDescription>
              </Alert>
            </div>
          )}

          <div
            ref={threadScrollRef}
            onScroll={handleThreadScroll}
            className={cn(CHAT_THREAD_SCROLL_CLASS, 'min-h-0 max-h-none flex-1')}
            data-testid="chat-thread-scroll"
          >
            <div className="space-y-4 p-4 sm:p-5">
              {!selectedId && (
                <div className="mx-auto mt-8 max-w-md rounded-2xl border border-dashed border-cp-line bg-cp-inset p-10 text-center shadow-inner">
                  <Brain className="mx-auto mb-4 h-14 w-14 text-cp-faint drop-shadow-md" />
                  <p className="font-semibold text-cp-fg">Select a session</p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Choose a session from the list on the left, or click <strong>New chat</strong> to start one.
                  </p>
                </div>
              )}
              {selectedId && threadLoading && (
                <div className="flex flex-col items-center justify-center gap-3 py-20">
                  <Loader2 className="h-10 w-10 animate-spin text-cp-violet" />
                  <span className="text-sm text-cp-muted">Loading thread…</span>
                </div>
              )}
              {selectedId &&
                !threadLoading &&
                visibleThreadMessages.map((msg, i) => (
                  <ThreadBubble
                    key={`${selectedId}-${i}-${msg.role}`}
                    role={msg.role}
                    content={msg.content}
                    index={i}
                    displayContent={displayChatMessageContent(
                      msg.content,
                      chatMessageDisplayOpts(msg, i, visibleThreadMessages, {
                        role: msg.role,
                        generatedCode: msg.generatedCode ?? chatGeneratedCode,
                        phase: msg.phase ?? chatPhase,
                        intent: effectiveIntent,
                        artifactType: panelArtifactType,
                        artifactUrl: resolvedArtifactUrl,
                        railSummary: railSummaryForPanel,
                        panelBlockCount: railPanelBlocks?.length || 0,
                        railHasContent: showDeliverablePanel,
                      })
                    )}
                  />
                ))}
              {showWorkingIndicator && (
                <AssistantWorkingIndicator label={generatingLabel} />
              )}
              {chatFinished && !chatGenerating && (
                <p className="pl-1 text-xs text-muted-foreground">{doneFooterMessage}</p>
              )}
              <div ref={threadEndRef} />
            </div>
          </div>

          {startError && (
            <div className="px-4 pb-0 pt-2 sm:px-5">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle className="text-sm">Could not start chat</AlertTitle>
                <AlertDescription className="font-mono text-xs">{startError}</AlertDescription>
              </Alert>
            </div>
          )}
          <div className="shrink-0 border-t border-cp-line bg-cp-surface px-4 py-3 backdrop-blur-md sm:px-5">
            {selectedId ? (
              <>
                {chatExcelFile && (
                  <div className="mb-2 flex items-center gap-2 rounded-md border border-cp-ok bg-cp-inset px-3 py-1.5 text-xs text-cp-ok">
                    <FileSpreadsheet className="h-3.5 w-3.5 shrink-0 text-cp-ok" />
                    <span className="flex-1 truncate">{chatExcelFile.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setChatExcelFile(null);
                        if (chatFileInputRef.current) chatFileInputRef.current.value = '';
                      }}
                      className="text-cp-ok hover:text-cp-ok"
                      aria-label="Remove spec"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <Textarea
                    ref={chatInputRef}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    placeholder={
                      chatGenerating
                        ? 'Assistant is responding…'
                        : chatAwaitingUser
                          ? 'Reply to assistant…'
                          : 'Continue the conversation…'
                    }
                    disabled={!canSendMessage || chatSending || chatGenerating || !VIBE_CHAT_API_BASE}
                    rows={2}
                    className="min-h-[2.5rem] flex-1 resize-none rounded-xl border-cp-line bg-cp-surface text-sm shadow-sm focus-visible:ring-cp-violet"
                    data-testid="chat-reply-input"
                  />
                  <label
                    className={cn(
                      'flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border text-xs font-medium transition-colors',
                      chatExcelFile
                        ? 'border-cp-ok bg-cp-inset text-cp-ok'
                        : 'border-cp-line bg-cp-surface text-cp-violet hover:bg-cp-inset',
                      (chatSending || chatGenerating || !VIBE_CHAT_API_BASE) && 'pointer-events-none opacity-40'
                    )}
                    title="Attach Excel spec"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    <input
                      ref={chatFileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      onChange={(e) => setChatExcelFile(e.target.files?.[0] || null)}
                      disabled={!canSendMessage || chatSending || chatGenerating || !VIBE_CHAT_API_BASE}
                    />
                  </label>
                  <span title={permissionDeniedTitle(canSendMessage, 'send chat messages')}>
                    <Button
                      type="button"
                      size="icon"
                      onClick={sendReply}
                      disabled={
                        !canSendMessage ||
                        !chatInput.trim() ||
                        chatSending ||
                        chatGenerating ||
                        !VIBE_CHAT_API_BASE
                      }
                      className="h-10 w-10 shrink-0 rounded-xl bg-cp-grad shadow-md hover:opacity-95 disabled:opacity-40"
                      data-testid="chat-reply-send"
                    >
                      {chatSending ? (
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                      ) : (
                        <Send className="h-4 w-4 text-white" />
                      )}
                    </Button>
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center gap-3 py-2 text-sm text-cp-muted">
                <Sparkles className="h-4 w-4 text-cp-violet" />
                <span>
                  Select a session on the left or{' '}
                  <button
                    type="button"
                    className="font-semibold text-cp-violet underline-offset-2 hover:underline disabled:opacity-50"
                    onClick={startNewChat}
                    disabled={!canCreateChat || chatStarting || !VIBE_CHAT_API_BASE}
                    title={permissionDeniedTitle(canCreateChat, 'start a new chat')}
                  >
                    {chatStarting ? 'Starting…' : 'start a new chat'}
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* Reasoning popover — side-rail deliverables only (not workflow/NUP graph) */}
          {showReasoning && (
            <>
              <div
                className={cn(
                  'absolute inset-0 z-20 bg-black/25 transition-opacity duration-200',
                  chainExpanded ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
                )}
                onClick={() => setChainExpanded(false)}
                aria-hidden={!chainExpanded}
                data-testid="studio-reasoning-scrim"
              />
              <div
                className={cn(
                  'absolute bottom-3 top-3 z-30 flex w-[min(440px,calc(100%-1.75rem))] flex-col overflow-hidden rounded-xl border border-cp-line bg-cp-surface shadow-[0_12px_32px_rgba(0,0,0,.22)] transition-[right,opacity] duration-200 ease-out',
                  chainExpanded ? 'right-3 opacity-100' : 'pointer-events-none -right-[480px] opacity-0'
                )}
                data-testid="studio-chain-of-thought"
                role="dialog"
                aria-label="Chain of Thought"
                aria-hidden={!chainExpanded}
              >
                <div className="flex shrink-0 items-center gap-2.5 border-b border-cp-line px-4 py-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cp-inset text-cp-violet">
                    <Brain className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-cp-fg">Chain of Thought</div>
                    <div className="text-[11px] text-cp-muted">Agent reasoning for this chat</div>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-cp-muted"
                    onClick={() => setChainExpanded(false)}
                    aria-label="Close reasoning"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain p-4">
                  {chainSteps.length > 0 ? (
                    chainSteps.map((section, index) => (
                      <div
                        key={`cot-${index}`}
                        className="rounded-lg border border-cp-line bg-cp-inset px-3 py-2 text-xs leading-relaxed text-cp-fg whitespace-pre-wrap"
                      >
                        {section}
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-2 text-[11px] text-cp-muted">
                      {(threadLoading || (chatGenerating && showWorkingIndicator)) && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-cp-violet" />
                      )}
                      <span>
                        {threadLoading || (chatGenerating && showWorkingIndicator)
                          ? 'Collecting agent reasoning…'
                          : 'No reasoning steps yet for this chat.'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </main>

        {/* Drag handle */}
        {railOpen && (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize artifacts panel"
            onMouseDown={startRailResize}
            className="flex w-2 shrink-0 cursor-col-resize items-center justify-center rounded hover:bg-cp-violet/10"
            data-testid="vibe-studio-rail-resize"
            title="Drag to resize artifacts panel"
          >
            <GripVertical className="h-5 w-5 text-cp-muted" />
          </div>
        )}

        {/* Right rail — tabbed PRD / deliverable */}
        <aside
          ref={railPanelRef}
          className={cn(
            'flex min-h-0 shrink-0 flex-col overflow-hidden rounded-xl border border-cp-line bg-cp-surface',
            !railResizing && 'transition-[width,flex-basis] duration-200 ease-out'
          )}
          style={railWidthStyle}
          data-testid="vibe-studio-artifacts-rail"
        >
          <div className="relative z-10 shrink-0 border-b border-cp-line bg-cp-surface">
            <div className="flex items-center justify-between gap-2 px-2.5 py-2">
              {railOpen ? (
                <div className="relative z-10 flex min-w-0 flex-1 flex-wrap gap-1.5">
                  {showPrdArtifact && (
                    <button
                      type="button"
                      onClick={() => setRailTab('prd')}
                      className={cn(
                        'rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors',
                        railTab === 'prd'
                          ? 'bg-cp-inset text-cp-fg'
                          : 'text-cp-muted hover:bg-cp-inset/60 hover:text-cp-fg'
                      )}
                      data-testid="rail-tab-prd"
                    >
                      PRD document
                    </button>
                  )}
                  {multiDeliverableRailTabs ? (
                    railArtifactPills.map((art) => {
                      const tabActive =
                        railTab === art.id ||
                        (railTab === 'code' && selectedArtifactId === art.id);
                      return (
                        <button
                          key={art.id}
                          type="button"
                          onClick={() => {
                            setSelectedArtifactId(art.id);
                            setRailTab(art.id);
                            deliverableRailFocusRef.current = {
                              sessionId: selectedId,
                              artifactCount: railArtifactPills.length,
                            };
                            manualArtifactTabRef.current = {
                              sessionId: selectedId,
                              artifactId: art.id,
                            };
                          }}
                          className={cn(
                            'max-w-[10rem] truncate rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors',
                            tabActive
                              ? 'bg-cp-inset text-cp-fg'
                              : 'text-cp-muted hover:bg-cp-inset/60 hover:text-cp-fg'
                          )}
                          data-testid={`rail-tab-deliverable-${art.id}`}
                          title={deliverableTabLabelForArtifact(art, railArtifactPills)}
                        >
                          {deliverableTabLabelForArtifact(art, railArtifactPills)}
                        </button>
                      );
                    })
                  ) : (
                    <button
                      type="button"
                      onClick={() => setRailTab('code')}
                      className={cn(
                        'rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors',
                        railTab === 'code' || !showPrdArtifact
                          ? 'bg-cp-inset text-cp-fg'
                          : 'text-cp-muted hover:bg-cp-inset/60 hover:text-cp-fg'
                      )}
                      data-testid="rail-tab-code"
                    >
                      {codeTabLabel}
                    </button>
                  )}
                </div>
              ) : (
                <span className="sr-only">Artifacts</span>
              )}
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-7 w-7 shrink-0 text-cp-muted hover:bg-cp-inset"
                onClick={() => setRailOpen((o) => !o)}
                title={railOpen ? 'Collapse artifacts' : 'Expand artifacts'}
              >
                {railOpen ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {railOpen && (
            <div
              className={cn(
                'min-h-0 flex-1 p-3',
                showPrdArtifact && railTab === 'prd'
                  ? 'overflow-y-auto overscroll-contain'
                  : 'flex flex-col overflow-hidden'
              )}
            >
              {showPrdArtifact && railTab === 'prd' ? (
                <PrdArtifactCard
                  key={selectedId}
                  sessionId={selectedId}
                  phase={chatPhase}
                  prdAvailable={prdReady}
                  prdChecking={prdChecking && !prdReady}
                  intent={effectiveIntent}
                  confirmDocType={chatConfirmDocType}
                />
              ) : (
                <ChatDeliverablePanel
                  fillHeight
                  intent={effectiveIntent}
                  artifactType={panelArtifactType}
                  generatedCode={railGeneratedCode}
                  artifactSummary={railSummaryForPanel}
                  panelBlocks={railPanelBlocks}
                  artifactUrl={resolvedArtifactUrl}
                  workflowStage={railWorkflowStage}
                  workflowState={railWorkflowState}
                  chatAtDone={chatFinished}
                  deliverableUpdating={deliverablePanelUpdating}
                  showPanel={showDeliverablePanel}
                  deployAvailable={deployAvailable && deliverableKind === 'abap'}
                  deploying={deploying}
                  deployMessage={deployMessage}
                  canDeploy={canDeployChat}
                  onDeploy={handleDeployChatCode}
                  onCopy={copyDeliverable}
                  copied={copied}
                  showValidationHint={showValidationHint}
                  hideWorkflowGraph={showWorkflowBanner}
                />
              )}
            </div>
          )}

          {!railOpen && (
            <div className="flex flex-1 flex-col items-center gap-3 pt-3 text-cp-muted">
              <FileText className="h-4 w-4" />
              <Code2 className="h-4 w-4" />
              <PanelRight className="h-4 w-4" />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
